/* Admin pages for the advanced reschedule module:
   - Reschedule policy (quota limits per role)
   - All reschedule requests overview
   - Approval page: original vs final agreed slot, quota usage, conflicts,
     both-party agreement and the full discussion history. */
import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { useList, errMsg } from "@/lib/api";
import ResourcePage from "@/components/ResourcePage";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, X, AlertTriangle, ShieldCheck, Gauge, Unlock, MessageCircle, Eye } from "lucide-react";
import { SlotPicker, SourceBadge } from "@/pages/reschedule/ReschedulePages";
import { Label } from "@/components/ui/label";

export function ReschedulePolicyPage() {
  return (
    <ResourcePage
      testId="reschedule-policy-page"
      title="Reschedule Policy"
      description="Quota limits per role. A new policy supersedes the previous active one for that role. Over-limit requests are blocked, or held for admin override when allowed."
      endpoint="/reschedule-policies"
      createLabel="New policy"
      searchKeys={["policy_name", "applies_to_role"]}
      canEdit={false}
      columns={[
        { key: "policy_name", label: "Policy", render: (r) => <span className="font-medium">{r.policy_name}</span> },
        { key: "applies_to_role", label: "Applies to", render: (r) => <span className="capitalize">{r.applies_to_role}</span> },
        { key: "max_requests_per_month", label: "Per month", render: (r) => r.max_requests_per_month ?? "∞" },
        { key: "max_requests_per_year", label: "Per year", render: (r) => r.max_requests_per_year ?? "∞" },
        { key: "max_requests_per_term", label: "Per term", render: (r) => r.max_requests_per_term ?? "—" },
        { key: "allow_admin_override", label: "Override", render: (r) => <StatusBadge status={r.allow_admin_override ? "active" : "inactive"} /> },
        { key: "effective_start_date", label: "Effective from" },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={[
        { name: "policy_name", label: "Policy name", required: true, placeholder: "e.g. 2026 parent policy" },
        { name: "applies_to_role", label: "Applies to", type: "select", required: true, options: [
          { value: "parent", label: "Parent / Student" }, { value: "teacher", label: "Teacher" }] },
        { name: "max_requests_per_month", label: "Max per month", type: "number", int: true },
        { name: "max_requests_per_year", label: "Max per year", type: "number", int: true },
        { name: "max_requests_per_term", label: "Max per term (optional)", type: "number", int: true },
        { name: "allow_admin_override", label: "Allow admin override", type: "switch", defaultValue: true },
        { name: "effective_start_date", label: "Effective from", type: "date" },
        { name: "effective_end_date", label: "Effective to (optional)", type: "date" },
      ]}
      transform={(p) => ({ ...p, allow_admin_override: !!p.allow_admin_override })}
    />
  );
}

const ALL_STATUSES = ["draft", "awaiting_teacher_proposal", "pending_teacher_response", "pending_parent_response",
  "under_discussion", "pending_direct_discussion", "pending_admin_approval", "approved", "rejected", "cancelled"];

export function AdminRescheduleRequests() {
  const [status, setStatus] = useState("all");
  const { data: requests, loading } = useList(`/reschedule-requests${status !== "all" ? `?status=${status}` : ""}`, [status]);
  const navigate = useNavigate();

  return (
    <div data-testid="admin-reschedule-requests-page">
      <PageHeader title="Reschedule Requests"
        description="All requests across the school. Open one to see the discussion, quota usage and conflict status."
        actions={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-56" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        } />
      <div className="rounded-lg border bg-card overflow-x-auto">
        {loading ? <div className="p-12 flex justify-center"><Spinner /></div> :
          !requests || requests.length === 0 ? <div className="p-6"><EmptyState text="No requests for this filter." /></div> : (
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Student / class</TableHead><TableHead>Requested by</TableHead>
              <TableHead>Original</TableHead><TableHead>Agreed new</TableHead>
              <TableHead>Messages</TableHead>
              <TableHead>Quota</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody className="stagger-in">
              {requests.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/admin/reschedule-approval/${r.id}`)}
                  data-testid={`admin-reschedule-row-${r.id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{r.student_name}</p>
                    <p className="text-xs text-muted-foreground">{r.course_name} · {r.teacher_name}</p>
                    <SourceBadge source={r.source_type} />
                  </TableCell>
                  <TableCell className="text-sm capitalize">{r.requested_by_name}<br />
                    <span className="text-xs text-muted-foreground">{r.requested_by_role}</span></TableCell>
                  <TableCell className="font-mono text-xs">{r.original_date}<br />{r.original_start_time}–{r.original_end_time}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.final_agreed_date ? <>{r.final_agreed_date}<br />{r.final_agreed_start_time}–{r.final_agreed_end_time}</> : "—"}
                  </TableCell>
                  <TableCell>
                    {r.unread_messages > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary"
                        data-testid={`admin-unread-messages-${r.id}`}>
                        <MessageCircle className="h-3.5 w-3.5" />{r.unread_messages} new
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {r.quota_exceeded
                      ? <StatusBadge status={r.quota_override_used ? "pending" : "overdue"}
                          testId={`quota-flag-${r.id}`} />
                      : <StatusBadge status="active" />}
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export function AdminRescheduleApproval() {
  const { id } = useParams();
  const { data: detail, loading, reload } = useList(`/reschedule-requests/${id}`);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [finalOpen, setFinalOpen] = useState(false);
  const [finalSlot, setFinalSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading || !detail) return <Spinner full />;
  const q = detail.quota;

  const act = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      reload();
    } catch (e) {
      toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    } finally { setBusy(false); }
  };

  const approve = () => act(() => api.post(`/reschedule-requests/${id}/approve`, { note: null }),
    "Approved — replacement booked, timetables and attendance updated.");
  const reject = () => {
    if (!rejectReason) { toast.error("Rejection reason is required"); return; }
    act(async () => {
      await api.post(`/reschedule-requests/${id}/reject`, { reason: rejectReason });
      setRejectOpen(false);
    }, "Rejected — original schedule unchanged.");
  };
  const override = () => {
    if (!overrideReason) { toast.error("Override reason is required (audit logged)"); return; }
    act(async () => {
      await api.post(`/reschedule-requests/${id}/override-quota`, { reason: overrideReason });
      setOverrideOpen(false);
    }, "Quota overridden — discussion unblocked.");
  };
  const acknowledge = () => act(() => api.post(`/reschedule-requests/${id}/acknowledge`),
    "Acknowledged — monitoring record saved.");
  const confirmFinal = () => {
    if (!finalSlot) { toast.error("Pick an available slot first"); return; }
    act(async () => {
      await api.post(`/reschedule-requests/${id}/confirm-final`, {
        date: finalSlot.date, start_time: finalSlot.start_time,
        end_time: finalSlot.end_time, studio_id: finalSlot.studio_id,
        note: "Entered by admin after direct discussion",
      });
      setFinalOpen(false); setFinalSlot(null);
    }, "Final arrangement confirmed — timetables updated.");
  };

  const timeline = [
    ...detail.proposals.map((p) => ({ kind: "proposal", at: p.created_at, item: p })),
    ...detail.messages.map((m) => ({ kind: "message", at: m.created_at, item: m })),
  ].sort((a, b) => (a.at < b.at ? -1 : 1));

  return (
    <div data-testid="admin-reschedule-approval-page">
      <Link to="/admin/reschedule-requests" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> All reschedule requests
      </Link>
      <PageHeader
        title={`${detail.student_name} · ${detail.course_name}`}
        description={`Teacher ${detail.teacher_name} · requested by ${detail.requested_by_name} (${detail.requested_by_role}) · ${detail.reason}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={detail.status} testId="approval-status-badge" />
            <SourceBadge source={detail.source_type} />
            {detail.admin_approval_status === "auto_confirmed" && !detail.admin_acknowledged && (
              <Button onClick={acknowledge} disabled={busy} data-testid="admin-acknowledge-button">
                <Eye className="h-4 w-4 mr-1" />Acknowledge (monitoring)
              </Button>
            )}
            {detail.status === "pending_direct_discussion" && (
              <Button onClick={() => setFinalOpen(true)} data-testid="admin-confirm-final-button">
                <Check className="h-4 w-4 mr-1" />Enter final arrangement
              </Button>
            )}
            {detail.status === "draft" && detail.quota_exceeded && (
              <Button variant="outline" onClick={() => setOverrideOpen(true)} data-testid="override-quota-button">
                <Unlock className="h-4 w-4 mr-1" />Override quota
              </Button>
            )}
            {detail.status === "pending_admin_approval" && (
              <>
                <Button onClick={approve} disabled={busy || detail.conflicts.length > 0} data-testid="admin-approve-button">
                  <Check className="h-4 w-4 mr-1" />Approve
                </Button>
                <Button variant="outline" className="text-destructive border-destructive/30"
                  onClick={() => setRejectOpen(true)} data-testid="admin-reject-button">
                  <X className="h-4 w-4 mr-1" />Reject
                </Button>
              </>
            )}
          </div>
        } />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger-in">
        <div className="rounded-lg border bg-card p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Original</p>
          <p className="font-mono text-sm">{detail.original_date}</p>
          <p className="font-mono text-sm">{detail.original_start_time}–{detail.original_end_time}</p>
          <p className="text-xs text-muted-foreground">{detail.original_studio_name}</p>
        </div>
        <div className={`rounded-lg border p-4 ${detail.final_agreed_date ? "bg-accent border-primary/30" : "bg-card"}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            <ArrowRight className="h-3 w-3" />Final agreed
          </p>
          {detail.final_agreed_date ? (
            <>
              <p className="font-mono text-sm" data-testid="approval-final-slot">{detail.final_agreed_date}</p>
              <p className="font-mono text-sm">{detail.final_agreed_start_time}–{detail.final_agreed_end_time}</p>
              <p className="text-xs text-muted-foreground">{detail.final_agreed_studio_name}</p>
            </>
          ) : <p className="text-sm text-muted-foreground">Not agreed yet</p>}
        </div>
        <div className="rounded-lg border bg-card p-4" data-testid="approval-quota-card">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            <Gauge className="h-3 w-3" />Requester quota
          </p>
          {q.scope === "student_cycle" ? (
            <>
              <p className="text-sm">{q.used}/{q.limit} used this cycle</p>
              <p className="text-xs text-muted-foreground">cycle {q.cycle_start} → {q.cycle_end}</p>
              {detail.quota_counted === false && (
                <p className="text-xs mt-1 text-[#2D6A4F] font-semibold">Teacher-leave sourced — does NOT use student quota</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm">{q.month_used}/{q.month_limit ?? "∞"} this month</p>
              <p className="text-sm">{q.year_used}/{q.year_limit ?? "∞"} this year</p>
            </>
          )}
          {detail.quota_exceeded && (
            <p className="text-xs mt-1 font-semibold text-destructive">
              Exceeds limit {detail.quota_override_used ? `· overridden (${detail.quota_override_reason})` : "· override needed"}
            </p>
          )}
        </div>
        <div className="rounded-lg border bg-card p-4" data-testid="approval-agreement-card">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" />Agreement & availability
          </p>
          <p className="text-sm">
            <span className={detail.agreed_by_parent ? "text-[#2D6A4F] font-semibold" : "text-muted-foreground"}>{detail.agreed_by_parent ? "✓" : "○"} Parent</span>
            {"  ·  "}
            <span className={detail.agreed_by_teacher ? "text-[#2D6A4F] font-semibold" : "text-muted-foreground"}>{detail.agreed_by_teacher ? "✓" : "○"} Teacher</span>
          </p>
          {detail.conflicts.length === 0 ? (
            <p className="text-xs mt-1 text-[#2D6A4F] font-semibold" data-testid="no-conflicts-indicator">No conflicts — slot is available</p>
          ) : (
            <p className="text-xs mt-1 text-destructive font-semibold" data-testid="conflicts-indicator">{detail.conflicts.length} conflict(s)!</p>
          )}
        </div>
      </div>

      {detail.conflicts.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-[#FFEDEC] p-4 mb-6 text-sm" data-testid="approval-conflict-warning">
          <p className="font-semibold flex items-center gap-1.5 text-destructive mb-1">
            <AlertTriangle className="h-4 w-4" />Conflicts on the agreed slot — approval is blocked
          </p>
          {detail.conflicts.map((c, i) => <p key={i} className="text-xs">{c}</p>)}
        </div>
      )}
      {detail.admin_rejected_reason && (
        <div className="rounded-lg border p-4 mb-6 text-sm bg-card">
          <span className="font-semibold">Rejection reason:</span> {detail.admin_rejected_reason}
        </div>
      )}

      <h3 className="text-lg font-semibold mb-3">Discussion history</h3>
      <div className="space-y-3" data-testid="approval-discussion-history">
        {timeline.length === 0 && <EmptyState text="No discussion recorded." />}
        {timeline.map(({ kind, item }) => kind === "proposal" ? (
          <div key={item.id} className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{item.proposed_by_name}</span> ({item.proposed_by_role}) proposed
              </p>
              <p className="font-mono text-sm">{item.proposed_date} · {item.proposed_start_time}–{item.proposed_end_time} · {item.proposed_studio_name}</p>
              {item.rejection_reason && <p className="text-xs text-destructive mt-1">Declined: {item.rejection_reason}</p>}
            </div>
            <StatusBadge status={item.proposal_status === "open" ? "pending" : item.proposal_status === "accepted" ? "approved" : item.proposal_status} />
          </div>
        ) : (
          <div key={item.id} className="rounded-lg border bg-card p-3 text-sm">
            <p className="text-xs text-muted-foreground mb-0.5">{item.sender_name} ({item.sender_role}) · {item.created_at?.slice(0, 16).replace("T", " ")}</p>
            <p>{item.message}</p>
          </div>
        ))}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="admin-reject-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject reschedule</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The original schedule will remain unchanged.</p>
          <Textarea data-testid="reject-reason-input" placeholder="Reason (sent to both parties)" value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="confirm-reject-button">Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader><DialogTitle className="font-display">Override reschedule quota</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This unblocks an over-limit request. The override is recorded in the audit log.
          </p>
          <Textarea data-testid="override-reason-input" placeholder="Override reason (required)" value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button onClick={override} disabled={busy} data-testid="confirm-override-button">Override & unblock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={finalOpen} onOpenChange={setFinalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-final-dialog">
          <DialogHeader><DialogTitle className="font-display">Enter final agreed arrangement</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Both parties agreed directly (thread / face-to-face). The system validates availability and confirms.
          </p>
          <SlotPicker scheduleId={detail.original_class_schedule_id} requestId={id} value={finalSlot} onChange={setFinalSlot} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalOpen(false)}>Cancel</Button>
            <Button onClick={confirmFinal} disabled={busy} data-testid="admin-submit-final-button">Confirm arrangement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
